import React from 'react'
import {Box} from '@mui/material'
import Card from 'src/components-new/layout/Card'
import DatePickerNew from 'src/components-new/inputs/DatePickerNew'
import RadioGroupNew from 'src/components-new/inputs/RadioGroupNew'
import ReadOnlyField from 'src/components-new/inputs/ReadOnlyField'
import {OPTIONAL_PRIVACY_CONSENTS, isOptionalConsentVisible} from 'src/constants/privacyConsents'

const YESNO_OPTIONS = [
  {value: 'true', label: 'Sì'},
  {value: 'false', label: 'No'},
]

function formatDate(value: string | null | undefined): string | null {
  if (!value) return null
  const d = new Date(value)
  if (isNaN(d.getTime())) return null
  return d.toLocaleDateString('it-IT', {day: 'numeric', month: 'long', year: 'numeric'})
}

interface ProgramMembershipSectionProps {
  values: Record<string, any>
  setField: (key: string, value: any) => void
  fields: Record<string, any>
}

export default function ProgramMembershipSection({values, setField, fields}: ProgramMembershipSectionProps): JSX.Element | null {
  if (
    fields?.enrollmentDate?.hidden &&
    fields?.receiveModuleDate?.hidden &&
    fields?.privacyConsents?.ENROLLMENT?.hidden &&
    fields?.privacyConsents?.PRIVACY_PHONE_SURVEY?.hidden &&
    fields?.privacyConsents?.PRIVACY_ENROLLMENT_APP_WEB?.hidden &&
    !OPTIONAL_PRIVACY_CONSENTS.some(({key}) => isOptionalConsentVisible(fields?.privacyConsents, key)) &&
    fields?.mds?.hidden &&
    fields?.testDBSService?.hidden
  ) {
    return null
  }

  return (
    <Card title="Dati adesione al programma">
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, 1fr)'},
          gap: 2,
          mb: 3,
        }}
      >
        {!fields?.enrollmentDate?.hidden && (
          fields?.enrollmentDate?.readOnly
            ? <ReadOnlyField label="Data di Adesione" value={formatDate(values.enrollmentDate ?? values.receiveModuleDate)} fullWidth />
            : (
              <DatePickerNew
                label="Data di Adesione"
                value={values.enrollmentDate ?? values.receiveModuleDate ?? null}
                onChange={v => setField('enrollmentDate', v)}
                fullWidth
              />
            )
        )}
        {!fields?.receiveModuleDate?.hidden && (
          fields?.receiveModuleDate?.readOnly
            ? <ReadOnlyField label="Data ricezione modulo" value={formatDate(values.receiveModuleDate)} fullWidth />
            : (
              <DatePickerNew
                label="Data ricezione modulo"
                value={values.receiveModuleDate ?? null}
                onChange={v => setField('receiveModuleDate', v)}
                fullWidth
              />
            )
        )}
      </Box>

      <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
        {!fields?.privacyConsents?.ENROLLMENT?.hidden && (
          <RadioGroupNew
            label="Per l'adesione al Programma"
            value={values.ENROLLMENT ?? 'false'}
            onChange={v => setField('ENROLLMENT', v)}
            options={YESNO_OPTIONS}
            disabled={fields?.privacyConsents?.ENROLLMENT?.readOnly}
          />
        )}
        {!fields?.privacyConsents?.PRIVACY_PHONE_SURVEY?.hidden && (
          <RadioGroupNew
            label="Per rilevazione telefonica finalizzata alla valutazione del gradimento dei servizi"
            value={values.PRIVACY_PHONE_SURVEY ?? 'false'}
            onChange={v => setField('PRIVACY_PHONE_SURVEY', v)}
            options={YESNO_OPTIONS}
            disabled={fields?.privacyConsents?.PRIVACY_PHONE_SURVEY?.readOnly}
          />
        )}
        {!fields?.privacyConsents?.PRIVACY_ENROLLMENT_APP_WEB?.hidden && (
          <RadioGroupNew
            label="Per la registrazione al portale/app"
            value={values.PRIVACY_ENROLLMENT_APP_WEB ?? 'false'}
            onChange={v => setField('PRIVACY_ENROLLMENT_APP_WEB', v)}
            options={YESNO_OPTIONS}
            disabled={fields?.privacyConsents?.PRIVACY_ENROLLMENT_APP_WEB?.readOnly}
          />
        )}
        {OPTIONAL_PRIVACY_CONSENTS
          .filter(({key}) => isOptionalConsentVisible(fields?.privacyConsents, key))
          .map(({key, label}) => (
            <RadioGroupNew
              key={key}
              label={label}
              value={values[key] ?? 'false'}
              onChange={v => setField(key, v)}
              options={YESNO_OPTIONS}
              disabled={fields?.privacyConsents?.[key]?.readOnly}
            />
          ))}
        {!fields?.mds?.hidden && (
          <RadioGroupNew
            label="Medico del Servizio"
            value={values.mds ?? ''}
            onChange={v => setField('mds', v)}
            options={YESNO_OPTIONS}
            disabled={fields?.mds?.readOnly}
          />
        )}
        {!fields?.testDBSService?.hidden && (
          <RadioGroupNew
            label="Servizio test DBS"
            value={values.testDBSService ?? ''}
            onChange={v => setField('testDBSService', v)}
            options={YESNO_OPTIONS}
            disabled={fields?.testDBSService?.readOnly}
          />
        )}
      </Box>
    </Card>
  )
}
